import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_TYPE_MAP,
  CALENDAR_COLORS,
  CALENDAR_KIND_DEFS,
  CALENDAR_KIND_MAP,
  EVENT_STATUS_MAP,
  WEEK_DAYS,
  type CalendarKind,
  type EventStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon, type IconName } from '../../components/Icon';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { ActivityDetail } from './ActivityDetail';

interface CalendarRow {
  id: string;
  name: string;
  kind: CalendarKind;
  kindLabel: string;
  color: string;
  capacity: number;
  preventOverlap: boolean;
  workingDays: string[];
  workStart: string;
  workEnd: string;
  isActive: boolean;
  eventCount: number;
}

interface EventRow {
  id: string;
  title: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  status: EventStatus;
  location: string | null;
  color: string | null;
  calendar: { id: string; name: string; color: string; kind: CalendarKind };
  account: { id: string; name: string } | null;
  deal: { id: string; title: string } | null;
  lead: { id: string; name: string } | null;
}

type View = 'month' | 'week' | 'day';

/** وحدةُ كل عرضٍ واسمُ «الآن» فيه — بهما يُسمّى شريط التنقّل. */
const PERIOD: Record<View, { unit: string; now: string }> = {
  month: { unit: 'الشهر', now: 'هذا الشهر' },
  week: { unit: 'الأسبوع', now: 'هذا الأسبوع' },
  day: { unit: 'اليوم', now: 'اليوم' },
};

/** نشاطٌ له موعد — كما يردّه سجلّ الأنشطة. */
interface ActivityLite {
  id: string;
  type: 'TASK' | 'MEETING' | 'CALL' | 'MAIL' | 'NOTE';
  subject: string;
  status: 'PLANNED' | 'DONE' | 'CANCELLED';
  dueAt: string | null;
  account: { id: string; name: string } | null;
  lead: { id: string; name: string } | null;
}

/**
 * ما يُرسم في خليّة اليوم — موعدٌ من تقويم أو نشاطٌ من سجلّ العميل.
 *
 * الشبكة كانت تعرض المواعيد وحدها، فمن يفتح التقويم ليرى يومه لا يجد
 * فيه مهامّه ولا مكالماته — وهي مواعيدُ عملٍ كسائرها. فوُحّد ما يُرسم،
 * ولكلٍّ بابُه عند النقر: الموعد يفتح محرّره، والنشاط يفتح تفاصيله.
 */
interface CalItem {
  key: string;
  kind: 'EVENT' | 'ACTIVITY';
  at: Date;
  title: string;
  color: string;
  hint: string;
  icon?: IconName;
  done?: boolean;
  event?: EventRow;
  activityId?: string;
}

/*
 * الأنشطة طبقةٌ لا لونًا خامسًا.
 *
 * ألوان الشبكة يختارها المستخدم لتقاويمه، فإقحامُ أربعة ألوانٍ للأنشطة
 * فوقها يوقع تصادمًا لا يُتوقَّع. فتُميَّز الأنشطة بشكلها (حدٌّ متقطّع)
 * وبأيقونة نوعها — ترميزٌ لا يعتمد اللون أصلًا.
 */
const ACTIVITY_ICON: Record<string, IconName> = {
  TASK: 'clipboard',
  MEETING: 'video',
  CALL: 'phone',
  MAIL: 'mail',
  NOTE: 'note',
};

/* ==========================================================================
   أدوات التاريخ
   ========================================================================== */

/**
 * بداية الأسبوع هي السبت.
 *
 * أسبوع العمل في مصر ومعظم المنطقة يبدأ بالسبت. بدؤه بالأحد يجعل يومي
 * العطلة مفصولين على طرفي الشبكة، فيصعب قراءة الأسبوع.
 */
function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() + 1) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function startOfMonthGrid(date: Date): Date {
  return startOfWeek(new Date(date.getFullYear(), date.getMonth(), 1));
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const monthFmt = new Intl.DateTimeFormat('ar-EG-u-nu-latn', { month: 'long', year: 'numeric' });
const dayFullFmt = new Intl.DateTimeFormat('ar-EG', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const dayShortFmt = new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
  day: 'numeric',
  month: 'long',
});

const dayNumFmt = new Intl.DateTimeFormat('ar-EG-u-nu-latn', { day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('ar-EG-u-nu-latn', {
  hour: '2-digit',
  minute: '2-digit',
});

const HOUR_START = 7;
const HOUR_END = 22;

/* ==========================================================================
   الشاشة
   ========================================================================== */

export function CalendarPage() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();

  const [view, setView] = useState<View>('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState<{ startAt: Date; endAt: Date } | null>(null);
  const [editingCalendars, setEditingCalendars] = useState(false);
  const [selected, setSelected] = useState<EventRow | null>(null);
  /* النشاط المفتوح من الشبكة — تفاصيله لا محرّر المواعيد. */
  const [openActivity, setOpenActivity] = useState<string | null>(null);
  /* طبقة الأنشطة تُطفأ كما يُطفأ تقويم — ومن أراد المواعيد وحدها أطفأها. */
  const [showActivities, setShowActivities] = useState(true);

  const calendars = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api<CalendarRow[]>('/calendar/calendars'),
  });

  const range = useMemo(() => {
    if (view === 'month') {
      const from = startOfMonthGrid(anchor);
      return { from, to: addDays(from, 42) };
    }
    /* عرض اليوم: نافذةٌ من أربعٍ وعشرين ساعة لا أسبوعٌ يُقلَّص. */
    if (view === 'day') {
      const from = new Date(anchor);
      from.setHours(0, 0, 0, 0);
      return { from, to: addDays(from, 1) };
    }
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 7) };
  }, [view, anchor]);

  const events = useQuery({
    queryKey: ['events', range.from.toISOString(), range.to.toISOString()],
    queryFn: () =>
      api<EventRow[]>(
        `/calendar/events${qs({ from: range.from.toISOString(), to: range.to.toISOString() })}`,
      ),
  });

  /* أنشطةُ المدى المعروض — بالمدى الصريح نفسه الذي يقرأ به التقويم. */
  const activities = useQuery({
    queryKey: ['activities', 'calendar', range.from.toISOString(), range.to.toISOString()],
    queryFn: () =>
      api<{ items: ActivityLite[] }>(
        `/crm/activities${qs({
          dueFrom: range.from.toISOString(),
          dueTo: range.to.toISOString(),
          pageSize: 100,
          sort: 'dueAt',
          order: 'asc',
        })}`,
      ),
  });

  const visible: CalItem[] = useMemo(() => {
    const fromEvents = (events.data ?? [])
      .filter((e) => !hidden.has(e.calendar.id))
      .map((e) => ({
        key: `e-${e.id}`,
        kind: 'EVENT' as const,
        at: new Date(e.startAt),
        title: e.title,
        color: e.color ?? e.calendar.color,
        hint: `${e.title} — ${e.calendar.name}`,
        event: e,
      }));

    if (!showActivities) return fromEvents;

    const fromActivities = (activities.data?.items ?? [])
      .filter((a) => a.dueAt)
      .map((a) => ({
        key: `a-${a.id}`,
        kind: 'ACTIVITY' as const,
        at: new Date(a.dueAt!),
        title: a.subject,
        color: 'var(--ink-3)',
        hint: `${ACTIVITY_TYPE_MAP[a.type]?.label ?? 'نشاط'}: ${a.subject}${a.lead || a.account ? ' — ' + (a.lead?.name ?? a.account?.name) : ''}`,
        icon: ACTIVITY_ICON[a.type] ?? 'clipboard',
        done: a.status === 'DONE',
        activityId: a.id,
      }));

    return [...fromEvents, ...fromActivities].sort((x, y) => x.at.getTime() - y.at.getTime());
  }, [events.data, activities.data, hidden, showActivities]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['events'] });
    void queryClient.invalidateQueries({ queryKey: ['calendars'] });
    setCreating(null);
    setSelected(null);
  };

  const move = (delta: number) => {
    const d = new Date(anchor);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    else if (view === 'day') d.setDate(d.getDate() + delta);
    else d.setDate(d.getDate() + delta * 7);
    setAnchor(d);
  };

  /*
   * اسم المدى المعروض — لكل عرضٍ صياغتُه:
   * الشهرُ باسمه وسنته، والأسبوعُ بطرفيه، واليومُ بيومه وتاريخه.
   */
  const periodLabel = useMemo(() => {
    if (view === 'month') return monthFmt.format(anchor);
    if (view === 'day') return dayShortFmt.format(anchor);
    const last = addDays(range.from, 6);
    return `${dayShortFmt.format(range.from)} — ${dayShortFmt.format(last)}`;
  }, [view, anchor, range.from]);

  /* حال الرؤية الجامعة: أكلُّها ظاهرٌ أم بعضُها؟ */
  const calIds = (calendars.data ?? []).map((c) => c.id);
  const allOn = showActivities && calIds.every((id) => !hidden.has(id));
  const someOn = showActivities || calIds.some((id) => !hidden.has(id));

  const noCalendars = calendars.data?.length === 0;

  return (
    <div className="page">
      <PageHead
        title="التقويم"
        subtitle={
          view === 'month'
            ? monthFmt.format(anchor)
            : view === 'day'
              ? dayFullFmt.format(anchor)
              : `أسبوع ${dayNumFmt.format(range.from)} — ${monthFmt.format(anchor)}`
        }
        actions={
          <>
            {/*
              أسماء التنقّل تتبع العرض.

              كان الزرّ بين السهمين يقول «اليوم» في كل الأحوال، والسهمان
              يتحرّكان بالشهر أو الأسبوع أو اليوم بحسب العرض — فيُقرأ
              الشريط كأنه يتنقّل بالأيام وحدها. صار كلٌّ يسمّي وحدته:
              «هذا الشهر» و«هذا الأسبوع» و«اليوم»، والسهمان بتلميحهما.
            */}
            <div className="row" style={{ gap: 2 }}>
              <button
                className="btn btn-icon"
                onClick={() => move(1)}
                aria-label={`${PERIOD[view].unit} التالي`}
                title={`${PERIOD[view].unit} التالي`}
              >
                <Icon name="chevronLeft" size={15} />
              </button>
              {/*
                الزرّ يسمّي المدى المعروض لا وحدتَه: «سبتمبر 2026» لا
                «هذا الشهر» — فمن تنقّل بالسهمين يعرف أين وقف بلا عدّ.
                والنقر يعيده إلى الآن، والتلميح يقولها.
              */}
              <button
                className="btn btn-sm"
                onClick={() => setAnchor(new Date())}
                title={`العودة إلى ${PERIOD[view].now}`}
              >
                {periodLabel}
              </button>
              <button
                className="btn btn-icon"
                onClick={() => move(-1)}
                aria-label={`${PERIOD[view].unit} السابق`}
                title={`${PERIOD[view].unit} السابق`}
              >
                <Icon name="chevronRight" size={15} />
              </button>
            </div>

            {/*
              التقاويمُ مرشِّحٌ بجوار مرشِّح التاريخ لا لوحةً جانبية.
              اللوحةُ كانت تأخذ عمودًا كاملًا من عرض الشاشة لقائمةٍ من خمسة
              أسطرٍ تُفتح مرّةً وتُترك — والشبكةُ هي ما يُقرأ. فصارت زرًّا
              يُفتح عند الحاجة وتبقى الشبكةُ بكامل العرض.
            */}
            <CalendarFilter
              calendars={calendars.data ?? []}
              hidden={hidden}
              setHidden={setHidden}
              showActivities={showActivities}
              setShowActivities={setShowActivities}
              allOn={allOn}
              someOn={someOn}
              canManage={atLeast('MANAGER')}
              onManage={() => setEditingCalendars(true)}
              noCalendars={noCalendars}
            />

            <div className="row" style={{ gap: 2 }}>
              <button
                className={`btn btn-sm${view === 'month' ? ' btn-primary' : ''}`}
                onClick={() => setView('month')}
              >
                شهر
              </button>
              <button
                className={`btn btn-sm${view === 'week' ? ' btn-primary' : ''}`}
                onClick={() => setView('week')}
              >
                أسبوع
              </button>
              {/* عرضُ اليوم: ساعاتُه وحدها، لمن يقرأ يومه ساعةً ساعة. */}
              <button
                className={`btn btn-sm${view === 'day' ? ' btn-primary' : ''}`}
                onClick={() => {
                  setView('day');
                  setAnchor(new Date());
                }}
              >
                يوم
              </button>
            </div>

            <button
              className="btn btn-primary"
              onClick={() => {
                const start = new Date();
                start.setMinutes(0, 0, 0);
                start.setHours(start.getHours() + 1);
                setCreating({ startAt: start, endAt: addHours(start, 1) });
              }}
              disabled={noCalendars}
              title={
                noCalendars
                  ? 'الموعد يُكتب في تقويم — أنشئ تقويمًا أولًا من ترس التقاويم'
                  : undefined
              }
            >
              <Icon name="plus" size={15} />
              موعد جديد
            </button>
          </>
        }
      />

      {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}

      {/*
        الشبكة تظهر دائمًا — ولو لم يُنشأ تقويمٌ بعد.

        التقويم العام ليس تقويمًا يُنشأ: هو الرؤية الجامعة، وأنشطةُ
        الملفات تملؤه بلا حاجةٍ إلى تقويمٍ أصلًا. وكان غيابُ التقاويم
        يحجب الشبكة كلَّها فتُخفى أنشطةٌ قائمة.
      */}
      {/* الشبكةُ بكامل العرض — التقاويمُ صارت مرشِّحًا في الشريط العلويّ. */}
      {view === 'month' ? (
        <MonthGrid
          anchor={anchor}
          from={range.from}
          events={visible}
          onPickDay={(day) => {
            const start = new Date(day);
            start.setHours(10, 0, 0, 0);
            setCreating({ startAt: start, endAt: addHours(start, 1) });
          }}
          onPickItem={(item) =>
            item.kind === 'EVENT' ? setSelected(item.event!) : setOpenActivity(item.activityId!)
          }
        />
      ) : (
        <WeekGrid
          from={range.from}
          days={view === 'day' ? 1 : 7}
          events={visible}
          onPickSlot={(start) => setCreating({ startAt: start, endAt: addHours(start, 1) })}
          onPickItem={(item) =>
            item.kind === 'EVENT' ? setSelected(item.event!) : setOpenActivity(item.activityId!)
          }
        />
      )}

      {/* نشاطٌ نُقر في الشبكة — تفاصيله كما تُفتح من أي شاشةٍ أخرى. */}
      {openActivity && <ActivityDetail id={openActivity} onClose={() => setOpenActivity(null)} />}

      {creating && (
        <EventModal
          calendars={calendars.data ?? []}
          initial={creating}
          onClose={() => setCreating(null)}
          onSaved={refresh}
        />
      )}

      {editingCalendars && (
        <CalendarsModal
          calendars={calendars.data ?? []}
          onClose={() => setEditingCalendars(false)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: ['calendars'] })}
        />
      )}

      {selected && (
        <EventDetailModal event={selected} onClose={() => setSelected(null)} onDeleted={refresh} />
      )}
    </div>
  );
}

function addHours(date: Date, hours: number): Date {
  const d = new Date(date);
  d.setHours(d.getHours() + hours);
  return d;
}

/* ==========================================================================
   مرشِّح التقاويم — زرٌّ في الشريط يفتح القائمة عند الحاجة
   ========================================================================== */

/**
 * ما كان لوحةً جانبية صار قائمةً منسدلة.
 *
 * التحدياتُ نفسها: التقويم العام (الرؤية الجامعة)، وكلُّ تقويم، وطبقةُ
 * الأنشطة. والزرُّ يقول حالَه بلا فتح: «الكل» حين لا شيءَ مخفيّ، وعددُ
 * الظاهر حين أُخفي بعضُها — فمن يرى شبكةً ناقصة يعرف لماذا من نظرة.
 */
function CalendarFilter({
  calendars,
  hidden,
  setHidden,
  showActivities,
  setShowActivities,
  allOn,
  someOn,
  canManage,
  onManage,
  noCalendars,
}: {
  calendars: CalendarRow[];
  hidden: Set<string>;
  setHidden: (next: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  showActivities: boolean;
  setShowActivities: (next: boolean | ((prev: boolean) => boolean)) => void;
  allOn: boolean;
  someOn: boolean;
  canManage: boolean;
  onManage: () => void;
  noCalendars: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  /* تُغلق بالنقر خارجها أو بـEscape — كأيّ قائمةٍ منسدلة. */
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /* كم طبقةً ظاهرة من كم — التقاويمُ والأنشطةُ معًا. */
  const total = calendars.length + 1;
  const shown = calendars.filter((c) => !hidden.has(c.id)).length + (showActivities ? 1 : 0);

  return (
    <div className="cal-filter" ref={root}>
      <button
        type="button"
        className={`btn btn-sm${allOn ? '' : ' cal-filter-partial'}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        title="اختر ما يظهر في الشبكة"
      >
        <Icon name="filter" size={14} />
        التقاويم
        <span className="tiny mute num">
          {allOn ? '' : someOn ? ` ${shown}/${total}` : ' لا شيء'}
        </span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={13} />
      </button>

      {open && (
        <div className="cal-filter-panel" role="group" aria-label="التقاويم الظاهرة">
          {/*
            التقويم العام ليس تقويمًا سادسًا يُنشأ ويُملأ: هو الرؤية
            الجامعة — كلُّ تقويمٍ وكلُّ نشاط. إطفاؤه يُخفي الكلَّ،
            وإشعالُه يُظهره، وما بينهما يُقرأ «بعضها».
          */}
          <label className="cal-toggle cal-master">
            <input
              type="checkbox"
              checked={allOn}
              ref={(el) => {
                if (el) el.indeterminate = !allOn && someOn;
              }}
              onChange={() => {
                if (allOn) {
                  setHidden(new Set(calendars.map((c) => c.id)));
                  setShowActivities(false);
                } else {
                  setHidden(new Set());
                  setShowActivities(true);
                }
              }}
            />
            <span className="cal-swatch is-master" aria-hidden />
            <span className="grow">
              <span className="small strong">التقويم العام</span>
              <span className="tiny mute" style={{ display: 'block' }}>
                كل المواعيد والحجوزات والأنشطة معًا
              </span>
            </span>
          </label>

          {noCalendars && (
            <p className="tiny mute" style={{ padding: 'var(--sp-2) 4px' }}>
              لا تقاويم بعد. أنشئ تقويمًا حين تحتاج رابط حجزٍ يُرسل للعملاء أو مواعيدَ تُكتب بيدك —
              والأنشطة تظهر في كل حال.
            </p>
          )}

          {calendars.map((c) => (
            <label className="cal-toggle" key={c.id}>
              <input
                type="checkbox"
                checked={!hidden.has(c.id)}
                onChange={() =>
                  setHidden((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id);
                    else next.add(c.id);
                    return next;
                  })
                }
              />
              <span className="cal-swatch" style={{ background: c.color }} aria-hidden />
              <span className="grow">
                <span className="small">{c.name}</span>
                <span className="tiny mute" style={{ display: 'block' }}>
                  {c.kindLabel}
                  {c.kind === 'RESOURCE' && c.capacity > 1 && ` — سعة ${c.capacity}`}
                </span>
              </span>
            </label>
          ))}

          {/*
            الأنشطة طبقةٌ فوق التقاويم لا تقويمًا بينها: مصدرها سجلّ
            العميل لا جدول المواعيد، فتُطفأ وحدها ولا تُخلط بها.
          */}
          <label className="cal-toggle cal-layer">
            <input
              type="checkbox"
              checked={showActivities}
              onChange={() => setShowActivities((v) => !v)}
            />
            <span className="cal-swatch is-activity" aria-hidden />
            <span className="grow">
              <span className="small">الأنشطة</span>
              <span className="tiny mute" style={{ display: 'block' }}>
                مهامٌّ واجتماعاتٌ ومكالماتٌ ورسائل — من ملفات العملاء
              </span>
            </span>
          </label>

          {canManage && (
            <button
              type="button"
              className="btn btn-sm btn-ghost cal-filter-manage"
              onClick={() => {
                setOpen(false);
                onManage();
              }}
            >
              <Icon name="settings" size={14} />
              إدارة التقاويم
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   عرض الشهر
   ========================================================================== */

function MonthGrid({
  anchor,
  from,
  events,
  onPickDay,
  onPickItem,
}: {
  anchor: Date;
  from: Date;
  events: CalItem[];
  onPickDay: (day: Date) => void;
  onPickItem: (item: CalItem) => void;
}) {
  const today = new Date();
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i));

  return (
    <div className="card">
      <div className="cal-grid">
        {WEEK_DAYS.map((d) => (
          <div className="cal-head-cell" key={d.key}>
            {d.label}
          </div>
        ))}

        {days.map((day) => {
          const dayEvents = events.filter((e) => sameDay(e.at, day));
          const outside = day.getMonth() !== anchor.getMonth();
          return (
            <div
              className={`cal-cell${outside ? ' cal-outside' : ''}${sameDay(day, today) ? ' cal-today' : ''}`}
              key={day.toISOString()}
              onClick={() => onPickDay(day)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onPickDay(day);
              }}
            >
              <div className="cal-day-num num">{dayNumFmt.format(day)}</div>
              <div className="cal-events">
                {dayEvents.slice(0, 4).map((e) => (
                  <button
                    key={e.key}
                    className={`cal-chip${e.kind === 'ACTIVITY' ? ' is-activity' : ''}${e.done ? ' is-done' : ''}`}
                    style={{ ['--chip-color' as string]: e.color }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onPickItem(e);
                    }}
                    title={e.hint}
                  >
                    {e.icon ? (
                      <Icon name={e.icon} size={11} />
                    ) : (
                      <span className="cal-chip-time num">{timeFmt.format(e.at)}</span>
                    )}
                    <span className="cal-chip-title">{e.title}</span>
                  </button>
                ))}
                {dayEvents.length > 4 && (
                  <span className="tiny mute">و{dayEvents.length - 4} غيرها</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
   عرض الأسبوع
   ========================================================================== */

function WeekGrid({
  from,
  days: dayCount = 7,
  events,
  onPickSlot,
  onPickItem,
}: {
  from: Date;
  /** سبعةٌ للأسبوع وواحدٌ لليوم — الشبكة واحدةٌ لا شبكتان تفترقان. */
  days?: number;
  events: CalItem[];
  onPickSlot: (start: Date) => void;
  onPickItem: (item: CalItem) => void;
}) {
  const today = new Date();
  const days = Array.from({ length: dayCount }, (_, i) => addDays(from, i));
  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

  return (
    <div className="card">
      <div className="week-grid" style={{ ['--week-cols' as string]: dayCount }}>
        <div className="week-corner" />
        {days.map((day) => (
          <div
            className={`cal-head-cell${sameDay(day, today) ? ' cal-today' : ''}`}
            key={day.toISOString()}
          >
            {WEEK_DAYS.find((d) => d.index === day.getDay())?.label}
            <span className="num tiny mute" style={{ display: 'block' }}>
              {dayNumFmt.format(day)}
            </span>
          </div>
        ))}

        {hours.map((hour) => (
          <>
            <div className="week-hour num" key={`h-${hour}`}>
              {String(hour).padStart(2, '0')}:00
            </div>
            {days.map((day) => {
              const slotStart = new Date(day);
              slotStart.setHours(hour, 0, 0, 0);
              const slotEnd = addHours(slotStart, 1);

              /* الموعد يظهر في الساعة التي يبدأ فيها فقط، لا في كل ساعة يمتد إليها. */
              const slotEvents = events.filter((e) => e.at >= slotStart && e.at < slotEnd);

              return (
                <div
                  className="week-cell"
                  key={`${day.toISOString()}-${hour}`}
                  onClick={() => onPickSlot(slotStart)}
                  role="button"
                  tabIndex={-1}
                >
                  {slotEvents.map((e) => (
                    <button
                      key={e.key}
                      className={`week-event${e.kind === 'ACTIVITY' ? ' is-activity' : ''}${e.done ? ' is-done' : ''}`}
                      style={{ ['--chip-color' as string]: e.color }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onPickItem(e);
                      }}
                      title={e.hint}
                    >
                      <span className="cal-chip-title">
                        {e.icon && <Icon name={e.icon} size={11} />} {e.title}
                      </span>
                      <span className="tiny num">
                        {timeFmt.format(e.at)}
                        {e.event ? ` — ${timeFmt.format(new Date(e.event.endAt))}` : ''}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
   نموذج الموعد
   ========================================================================== */

function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface Availability {
  available: boolean;
  capacity: number;
  booked: number;
  conflicts: Array<{ id: string; title: string; startAt: string; endAt: string }>;
  warning?: string;
}

function EventModal({
  calendars,
  initial,
  onClose,
  onSaved,
}: {
  calendars: CalendarRow[];
  initial: { startAt: Date; endAt: Date };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [calendarId, setCalendarId] = useState(calendars[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [startAt, setStartAt] = useState(toLocalInput(initial.startAt));
  const [endAt, setEndAt] = useState(toLocalInput(initial.endAt));
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');

  const accounts = useQuery({
    queryKey: ['accounts', 'options'],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: 'name', order: 'asc' })}`,
      ),
  });

  /*
   * فحص الإتاحة يجري مع تغيّر الوقت لا عند الحفظ.
   *
   * معرفة التعارض بعد ملء النموذج كله تعني إعادة العمل. معرفته أثناء اختيار
   * الوقت تعني اختيار وقت آخر مباشرة.
   */
  const availability = useQuery({
    queryKey: ['availability', calendarId, startAt, endAt],
    queryFn: () =>
      api<Availability>(
        `/calendar/events/availability${qs({
          calendarId,
          startAt: new Date(startAt).toISOString(),
          endAt: new Date(endAt).toISOString(),
        })}`,
      ),
    enabled: !!calendarId && !!startAt && !!endAt && new Date(endAt) > new Date(startAt),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/calendar/events', {
        method: 'POST',
        body: {
          calendarId,
          title: title.trim(),
          description: description || undefined,
          location: location || undefined,
          startAt: new Date(startAt).toISOString(),
          endAt: new Date(endAt).toISOString(),
          accountId: accountId || undefined,
        },
      }),
    onSuccess: onSaved,
  });

  const calendar = calendars.find((c) => c.id === calendarId);
  const blocked = availability.data && !availability.data.available;
  const canSave =
    calendarId &&
    title.trim().length >= 2 &&
    new Date(endAt) > new Date(startAt) &&
    !blocked &&
    !create.isPending;

  return (
    <Modal
      title="موعد جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSave}>
            {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="col">
        <div className="field">
          <label className="label req" htmlFor="e-calendar">
            التقويم
          </label>
          <select
            id="e-calendar"
            className="select"
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
          >
            {calendars.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.kindLabel}
              </option>
            ))}
          </select>
          {calendar?.preventOverlap && (
            <span className="tiny mute">
              هذا التقويم يمنع الحجز المزدوج. السعة {calendar.capacity}، وساعات العمل من{' '}
              {calendar.workStart} إلى {calendar.workEnd}.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label req" htmlFor="e-title">
            العنوان
          </label>
          <input
            id="e-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="جلسة تصوير — شركة النور"
            autoFocus
          />
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="label req" htmlFor="e-start">
              من
            </label>
            <input
              id="e-start"
              className="input"
              type="datetime-local"
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label req" htmlFor="e-end">
              إلى
            </label>
            <input
              id="e-end"
              className="input"
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
            />
          </div>
        </div>

        {availability.data && (
          <div
            className={`alert ${
              blocked ? 'alert-danger' : availability.data.warning ? 'alert-warning' : 'alert-info'
            }`}
          >
            <Icon
              name={blocked ? 'alert' : availability.data.warning ? 'alert' : 'check'}
              size={18}
            />
            <div>
              {blocked ? (
                <>
                  الوقت محجوز: {availability.data.conflicts.map((c) => c.title).join('، ')}. اختر
                  وقتًا آخر.
                </>
              ) : availability.data.warning ? (
                <>{availability.data.warning} الحجز ممكن، لكن تأكد أنه مقصود.</>
              ) : (
                'الوقت متاح.'
              )}
            </div>
          </div>
        )}

        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="e-account">
              الحساب المرتبط
            </label>
            <select
              id="e-account"
              className="select"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">بلا ربط</option>
              {accounts.data?.items.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="e-location">
              المكان
            </label>
            <input
              id="e-location"
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-desc">
            التفاصيل
          </label>
          <textarea
            id="e-desc"
            className="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   تفاصيل الموعد
   ========================================================================== */

function EventDetailModal({
  event,
  onClose,
  onDeleted,
}: {
  event: EventRow;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => api(`/calendar/events/${event.id}`, { method: 'DELETE' }),
    onSuccess: onDeleted,
  });

  const status = EVENT_STATUS_MAP[event.status];

  return (
    <Modal
      title={event.title}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إغلاق
          </button>
          <button
            className="btn btn-danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'جارٍ الحذف' : 'حذف الموعد'}
          </button>
        </>
      }
    >
      {remove.isError && (
        <div className="alert alert-danger mb-4">
          {remove.error instanceof Error ? remove.error.message : 'تعذّر الحذف.'}
        </div>
      )}

      <dl className="detail-list">
        <dt>التقويم</dt>
        <dd>
          <span className="row">
            <span className="cal-swatch" style={{ background: event.calendar.color }} aria-hidden />
            {event.calendar.name}
          </span>
        </dd>

        <dt>الوقت</dt>
        <dd className="num">
          {timeFmt.format(new Date(event.startAt))} — {timeFmt.format(new Date(event.endAt))}
        </dd>

        <dt>الحالة</dt>
        <dd>
          <Badge kind={status?.kind}>{status?.label}</Badge>
        </dd>

        {event.location && (
          <>
            <dt>المكان</dt>
            <dd>{event.location}</dd>
          </>
        )}

        {event.account && (
          <>
            <dt>الحساب</dt>
            <dd>
              <Link to={`/crm/accounts/${event.account.id}`}>{event.account.name}</Link>
            </dd>
          </>
        )}

        {event.deal && (
          <>
            <dt>الصفقة</dt>
            <dd>{event.deal.title}</dd>
          </>
        )}
      </dl>
    </Modal>
  );
}

/* ==========================================================================
   إدارة التقاويم
   ========================================================================== */

function CalendarsModal({
  calendars,
  onClose,
  onSaved,
}: {
  calendars: CalendarRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CalendarKind>('RESOURCE');
  const [color, setColor] = useState<string>(CALENDAR_COLORS[0]);
  const [capacity, setCapacity] = useState('1');
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('18:00');
  const [days, setDays] = useState<string[]>(['sat', 'sun', 'mon', 'tue', 'wed', 'thu']);

  /* التقويم المطلوب حذفه — يُسأل عنه قبل أن يُمسّ. */
  const [confirming, setConfirming] = useState<CalendarRow | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true; deactivated: boolean }>(`/calendar/calendars/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: (res) => {
      setConfirming(null);
      onSaved();
      toast(
        res.deactivated ? 'له مواعيد فعُطّل ولم يُمحَ — مواعيدُه تبقى في تاريخها' : 'حُذف التقويم',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  const create = useMutation({
    mutationFn: () =>
      api('/calendar/calendars', {
        method: 'POST',
        body: {
          name: name.trim(),
          kind,
          color,
          capacity: Number(capacity) || 1,
          preventOverlap: kind === 'RESOURCE',
          workingDays: days,
          workStart,
          workEnd,
        },
      }),
    onSuccess: () => {
      setName('');
      onSaved();
    },
  });

  return (
    <Modal
      title="التقاويم"
      onClose={onClose}
      wide
      footer={
        <button className="btn" onClick={onClose} type="button">
          إغلاق
        </button>
      }
    >
      {calendars.length > 0 && (
        <div className="table-wrap mb-4">
          <table className="table">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>النوع</th>
                <th>السعة</th>
                <th>ساعات العمل</th>
                <th>المواعيد</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {calendars.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="row">
                      <span className="cal-swatch" style={{ background: c.color }} aria-hidden />
                      <span className="cell-strong">{c.name}</span>
                    </span>
                  </td>
                  <td>
                    <Badge kind={CALENDAR_KIND_MAP[c.kind]?.kind}>{c.kindLabel}</Badge>
                  </td>
                  <td className="num">{c.capacity}</td>
                  <td className="num cell-mute">
                    {c.workStart} — {c.workEnd}
                  </td>
                  <td className="num cell-mute">{c.eventCount}</td>
                  {/*
                    الحذف من هنا، والخادم هو الحكم: تقويمٌ بلا مواعيد يُمحى،
                    وذو المواعيد يُعطَّل ولا يُمحى — فمواعيدُه المنعقدة تبقى
                    في تاريخها. والردّ يقول أيُّهما وقع.
                  */}
                  <td className="end">
                    <button
                      className="btn btn-sm btn-icon btn-danger-hover"
                      disabled={remove.isPending}
                      onClick={() => setConfirming(c)}
                      title="حذف التقويم"
                      aria-label={`حذف ${c.name}`}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        التأكيد قبل المسّ — والنصّ يقول ما سيقع فعلًا لا وعدًا عامًّا:
        بلا مواعيد يُمحى، وبمواعيدَ يُعطَّل وتبقى مواعيدُه في تاريخها.
      */}
      {confirming && (
        <Modal
          title={`حذف ${confirming.name}`}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirming(null)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate(confirming.id)}
              >
                {remove.isPending ? 'جارٍ الحذف' : confirming.eventCount > 0 ? 'عطّله' : 'احذفه'}
              </button>
            </>
          }
        >
          <p className="small">
            {confirming.eventCount > 0
              ? `على هذا التقويم ${confirming.eventCount} موعدًا، فلن يُمحى بل يُعطَّل: تختفي مواعيدُه من الشبكة ويبقى سجلُّها في تاريخه، ولا يُحجز عليه بعدها.`
              : 'لا مواعيد على هذا التقويم، فيُمحى محوًا تامًّا.'}
          </p>
          {confirming.eventCount === 0 && (
            <p className="tiny mute mt-4">وصفحاتُ الحجز المربوطة به — إن وُجدت — تُحذف معه.</p>
          )}
        </Modal>
      )}
      {/*
        التقويم المخصَّص نصفُ الطريق: يفتح الحجزَ عليه صفحةُ حجزٍ تُنشأ
        له — استوديو يُحجز، أو خدمةٌ لها مواعيد. فالباب مذكورٌ هنا لا
        يُترك ليُكتشف.
      */}
      <p className="tiny mute mb-4">
        تقويمٌ للموارد (استوديو، قاعة، خدمة) يصير قابلًا للحجز حين تُنشأ له{' '}
        <Link to="/crm/booking" className="link">
          صفحة حجز
        </Link>{' '}
        — تُحدَّد فيها المدة وساعات الإتاحة، ويحجز العميل بنفسه فيقع الموعد في هذا التقويم ويُفحص
        عليه التعارض.
      </p>

      <h3 className="label mb-4">تقويم جديد</h3>

      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="col">
        <div className="field">
          <span className="label">النوع</span>
          <div className="col" style={{ gap: 'var(--sp-2)' }}>
            {CALENDAR_KIND_DEFS.map((k) => (
              <label className="cal-kind-option" key={k.key}>
                <input
                  type="radio"
                  name="cal-kind"
                  checked={kind === k.key}
                  onChange={() => setKind(k.key)}
                />
                <span>
                  <span className="strong small">{k.label}</span>
                  <span className="tiny mute" style={{ display: 'block' }}>
                    {k.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-grid">
          <div className="field span-2">
            <label className="label req" htmlFor="c-name">
              الاسم
            </label>
            <input
              id="c-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'RESOURCE' ? 'استوديو التصوير الرئيسي' : 'مواعيد فريق المبيعات'}
            />
          </div>

          {kind === 'RESOURCE' && (
            <div className="field">
              <label className="label" htmlFor="c-capacity">
                السعة
              </label>
              <input
                id="c-capacity"
                className="input num"
                type="number"
                min="1"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
              <span className="tiny mute">عدد الحجوزات المتزامنة المسموح بها.</span>
            </div>
          )}

          <div className="field">
            <label className="label" htmlFor="c-start">
              بداية العمل
            </label>
            <input
              id="c-start"
              className="input"
              type="time"
              value={workStart}
              onChange={(e) => setWorkStart(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="c-end">
              نهاية العمل
            </label>
            <input
              id="c-end"
              className="input"
              type="time"
              value={workEnd}
              onChange={(e) => setWorkEnd(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <span className="label">أيام العمل</span>
          <div className="row" style={{ gap: 2, flexWrap: 'wrap' }}>
            {WEEK_DAYS.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`btn btn-sm${days.includes(d.key) ? ' btn-primary' : ''}`}
                onClick={() =>
                  setDays((prev) =>
                    prev.includes(d.key) ? prev.filter((x) => x !== d.key) : [...prev, d.key],
                  )
                }
                aria-pressed={days.includes(d.key)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <span className="label">اللون</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {CALENDAR_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`color-dot${color === c ? ' selected' : ''}`}
                style={{ background: c }}
                onClick={() => setColor(c)}
                aria-label={`اللون ${c}`}
                aria-pressed={color === c}
              />
            ))}
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => create.mutate()}
          disabled={name.trim().length < 2 || days.length === 0 || create.isPending}
        >
          {create.isPending ? 'جارٍ الإنشاء' : 'إنشاء التقويم'}
        </button>
      </div>
    </Modal>
  );
}
